import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Divider,
  Drawer,
  Typography,
  Empty,
  Descriptions,
  Progress,
  Tooltip,
  message,
} from "antd";
import {
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  readCampaign,
  getCampaignReportCounts,
  downloadCampaignReport,
} from "../../redux/actions/campaignsAction";

import FormCampaign from "../../components/forms/formCampaign";
import DeleteRecord from "../../components/modals/deleteRecord";
import SendEmail from "../../components/modals/sendEmail";

const { Title, Text, Paragraph } = Typography;

export default function ViewCampaign({ campaignId, fetchRecords }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [report, setReport] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingProcessedReport, setDownloadingProcessedReport] =
    useState(false);
  const [downloadingSummaryReport, setDownloadingSummaryReport] =
    useState(false);
  const [downloadingExcludedReport, setDownloadingExcludedReport] =
    useState(false);

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };
  const sendEmailAndClose = () => {
    fetchRecords();
    onClose();
  };

  useEffect(() => {
    if (open) {
      dispatch(readCampaign({}, campaignId))
        .then((response) => {
          setCampaign(response);
        })
        .catch((e) => console.log(e));
    } else {
      setReport(null);
    }
  }, [open, campaignId, dispatch]);

  useEffect(() => {
    if (open && campaign?.status?.description === "Sent") {
      dispatch(getCampaignReportCounts(campaignId))
        .then((res) => setReport(res.data?.result))
        .catch((e) => console.log(e));
    }
  }, [open, campaign, campaignId, dispatch]);

  const XLSX_MIME_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const downloadBounceReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await dispatch(downloadCampaignReport(campaignId, "bounced"));
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: XLSX_MIME_TYPE }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `campaign-${campaignId}-bounce-report.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download bounce report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadProcessedReport = async () => {
    setDownloadingProcessedReport(true);
    try {
      const res = await dispatch(
        downloadCampaignReport(campaignId, "processed"),
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: XLSX_MIME_TYPE }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `campaign-${campaignId}-processed-report.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download processed report");
    } finally {
      setDownloadingProcessedReport(false);
    }
  };

  const downloadExcludedReport = async () => {
    setDownloadingExcludedReport(true);
    try {
      const res = await dispatch(
        downloadCampaignReport(campaignId, "excluded"),
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: XLSX_MIME_TYPE }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `campaign-${campaignId}-excluded-report.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download excluded report");
    } finally {
      setDownloadingExcludedReport(false);
    }
  };

  const downloadSummaryReport = async () => {
    setDownloadingSummaryReport(true);
    try {
      const res = await dispatch(downloadCampaignReport(campaignId, "summary"));
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: XLSX_MIME_TYPE }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `campaign-${campaignId}-summary-report.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download campaign report");
    } finally {
      setDownloadingSummaryReport(false);
    }
  };

  return (
    <>
      <Button type="text" onClick={showDrawer} title="View Campaign">
        <EyeOutlined />
      </Button>
      <Drawer
        title="View Campaign"
        width="min(820px, 90vw)"
        placement="right"
        maskClosable={true}
        destroyOnClose={true}
        onClose={onClose}
        open={open}
        extra={
          !campaign
            ? []
            : [
                <DeleteRecord
                  id={campaign._id}
                  collection="campaigns"
                  fetchRecords={fetchRecords}
                />,
                campaign.status.description === "Draft" && (
                  <FormCampaign
                    type="edit"
                    formData={campaign}
                    fetchRecords={fetchRecords}
                  />
                ),
                campaign.status.description === "Draft" && (
                  <SendEmail
                    id={campaign._id}
                    subject={campaign.subject}
                    fetchRecords={sendEmailAndClose}
                  />
                ),
              ]
        }
      >
        {!campaign ? (
          <Empty
            description={<Paragraph>There is no data to display.</Paragraph>}
          />
        ) : (
          <>
            <Space
              direction="vertical"
              size="large"
              style={{ display: "flex" }}
            >
              {campaign.status.description === "Sent" && (
                <>
                  <Row gutter={[30, 30]}>
                    <Col xs={24} md={12}>
                      <Card bordered={true} style={{ display: "block" }}>
                        <Statistic
                          title={"Opens (" + (campaign.uniqueOpens || 0) + ")"}
                          value={
                            ((campaign.uniqueOpens || 0) /
                              campaign.totalContacts) *
                            100
                          }
                          valueStyle={{
                            color:
                              ((campaign.uniqueOpens || 0) /
                                campaign.totalContacts) *
                                100 >
                              50
                                ? "#3f8600"
                                : "#cf1322",
                          }}
                          precision={2}
                          prefix={
                            ((campaign.uniqueOpens || 0) /
                              campaign.totalContacts) *
                              100 >
                            50 ? (
                              <ArrowUpOutlined />
                            ) : (
                              <ArrowDownOutlined />
                            )
                          }
                          suffix="%"
                        />
                      </Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card bordered={true} style={{ display: "block" }}>
                        <Statistic
                          title={
                            "Clicks (" + (campaign.uniqueClicks || 0) + ")"
                          }
                          value={
                            ((campaign.uniqueClicks || 0) /
                              campaign.totalContacts) *
                            100
                          }
                          valueStyle={{
                            color:
                              ((campaign.uniqueClicks || 0) /
                                campaign.totalContacts) *
                                100 >
                              (((campaign.uniqueOpens || 0) /
                                campaign.totalContacts) *
                                100) /
                                2
                                ? "#3f8600"
                                : "#cf1322",
                          }}
                          precision={2}
                          prefix={
                            ((campaign.uniqueClicks || 0) /
                              campaign.totalContacts) *
                              100 >
                            (((campaign.uniqueOpens || 0) /
                              campaign.totalContacts) *
                              100) /
                              2 ? (
                              <ArrowUpOutlined />
                            ) : (
                              <ArrowDownOutlined />
                            )
                          }
                          suffix="%"
                        />
                      </Card>
                    </Col>
                    <Col xs={24} md={8} style={{ display: "flex" }}>
                      <Card
                        bordered={true}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                        styles={{
                          body: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          },
                        }}
                      >
                        <Statistic
                          title="Processed"
                          value={campaign.totalSent}
                          suffix={
                            <Text>out of {campaign.totalContacts ?? 0}</Text>
                          }
                        />
                        <Progress
                          percent={(
                            (campaign.totalSent / campaign.totalContacts) *
                            100
                          ).toFixed(0)}
                        />
                        <div style={{ flex: 1 }} />
                        <Button
                          block
                          size="small"
                          loading={downloadingProcessedReport}
                          onClick={downloadProcessedReport}
                        >
                          Download
                        </Button>
                      </Card>
                    </Col>
                    <Col xs={24} md={8} style={{ display: "flex" }}>
                      <Card
                        bordered={true}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                        styles={{
                          body: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          },
                        }}
                      >
                        <Statistic
                          title={
                            <Space size={4}>
                              <Text>Bounced</Text>
                              <Tooltip title="Bounces are already included in Processed above, not additional recipients — they were accepted for delivery and failed afterward.">
                                <InfoCircleOutlined
                                  style={{ color: "rgba(0,0,0,0.45)" }}
                                />
                              </Tooltip>
                            </Space>
                          }
                          value={
                            (report?.counts?.bounced ?? 0) +
                            (report?.counts?.complained ?? 0)
                          }
                          suffix={
                            <Text>of {campaign.totalSent ?? 0} processed</Text>
                          }
                          valueStyle={{
                            color:
                              (report?.counts?.bounced ?? 0) +
                                (report?.counts?.complained ?? 0) >
                              0
                                ? "#cf1322"
                                : undefined,
                          }}
                        />
                        <Progress
                          percent={100}
                          status="exception"
                          success={{
                            percent: campaign.totalContacts
                              ? ((campaign.totalContacts -
                                  ((report?.counts?.bounced ?? 0) +
                                    (report?.counts?.complained ?? 0))) /
                                  campaign.totalContacts) *
                                100
                              : 100,
                          }}
                        />
                        <div style={{ flex: 1 }} />
                        <Button
                          block
                          size="small"
                          loading={downloadingReport}
                          disabled={!report}
                          onClick={downloadBounceReport}
                        >
                          Download
                        </Button>
                      </Card>
                    </Col>
                    <Col xs={24} md={8} style={{ display: "flex" }}>
                      <Card
                        bordered={true}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                        styles={{
                          body: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          },
                        }}
                      >
                        <Statistic
                          title="Excluded"
                          value={report?.excludedCount ?? 0}
                          suffix={<Text>by tag</Text>}
                        />
                        <div style={{ flex: 1 }} />
                        <Button
                          block
                          size="small"
                          loading={downloadingExcludedReport}
                          disabled={!report}
                          onClick={downloadExcludedReport}
                        >
                          Download
                        </Button>
                      </Card>
                    </Col>
                  </Row>
                  <Button
                    block
                    loading={downloadingSummaryReport}
                    onClick={downloadSummaryReport}
                  >
                    Download campaign report (Excel)
                  </Button>
                  <Divider />
                </>
              )}
              <Descriptions
                title={
                  <Text>
                    {campaign.totalContacts
                      ? "Total " + campaign.totalContacts + " Contacts"
                      : "No Contacts"}
                  </Text>
                }
                items={[
                  {
                    key: 1,
                    label: "Tags",
                    children: campaign.tags.toString(),
                  },
                  {
                    key: 2,
                    label:
                      campaign.status.description === "Sent"
                        ? "Sent On"
                        : "Created On",
                    children: new Date(
                      campaign.status.description === "Sent"
                        ? campaign.updatedAt
                        : campaign.createdAt
                    ).toLocaleString(),
                  },
                ]}
              />
              <Title level={5} style={{ textAlign: "center" }}>
                {campaign.subject}
              </Title>
              <div dangerouslySetInnerHTML={{ __html: campaign.message }}></div>
            </Space>
          </>
        )}
      </Drawer>
    </>
  );
}
